import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Trash2, 
  Copy, 
  Pause, 
  Play, 
  Clock, 
  Bell, 
  BarChart3, 
  Edit3,
  Calendar,
  AlertTriangle,
  Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { FilteringRule } from '@/types/social';

interface RuleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: FilteringRule | null;
  onRuleUpdated: () => void;
  onRuleDeleted: () => void;
}

const RuleSettingsDialog = ({ open, onOpenChange, rule, onRuleUpdated, onRuleDeleted }: RuleSettingsDialogProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [stats, setStats] = useState({ itemsFiltered: 0, lastTriggered: null });
  
  // General settings
  const [ruleName, setRuleName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState('medium');
  
  // Time settings
  const [hasTimeRestrictions, setHasTimeRestrictions] = useState(false);
  const [activeHours, setActiveHours] = useState({ start: '09:00', end: '17:00' });
  const [activeDays, setActiveDays] = useState<string[]>(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  
  // Notification settings
  const [enableNotifications, setEnableNotifications] = useState(false);
  const [notificationThreshold, setNotificationThreshold] = useState(5);
  
  useEffect(() => {
    if (rule) {
      setRuleName(rule.name);
      setIsActive(rule.is_active);
      loadRuleStats();
    }
  }, [rule]);

  const loadRuleStats = async () => {
    if (!rule) return;
    
    try {
      const { count } = await supabase
        .from('filtered_content')
        .select('*', { count: 'exact', head: true })
        .eq('rule_id', rule.id);
        
      const { data: recentFiltered } = await supabase
        .from('filtered_content')
        .select('filtered_at')
        .eq('rule_id', rule.id)
        .order('filtered_at', { ascending: false })
        .limit(1);
        
      setStats({
        itemsFiltered: count || 0,
        lastTriggered: recentFiltered?.[0]?.filtered_at || null
      });
    } catch (error) {
      console.error('Error loading rule stats:', error);
    }
  };

  const handleUpdateRule = async () => {
    if (!rule) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .update({
          name: ruleName,
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', rule.id);

      if (error) throw error;
      
      toast.success('Rule updated successfully');
      onRuleUpdated();
    } catch (error) {
      console.error('Error updating rule:', error);
      toast.error('Failed to update rule');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!rule) return;
    
    if (!confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .delete()
        .eq('id', rule.id);

      if (error) throw error;
      
      toast.success('Rule deleted successfully');
      onRuleDeleted();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateRule = async () => {
    if (!rule || !user) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .insert({
          user_id: user.id,
          name: `${rule.name} (Copy)`,
          rule_type: rule.rule_type,
          platforms: rule.platforms,
          keywords: rule.keywords,
          blocked_users: rule.blocked_users,
          blocked_hashtags: rule.blocked_hashtags,
          sentiment_filter: rule.sentiment_filter,
          is_active: false // Start duplicates as inactive
        });

      if (error) throw error;
      
      toast.success('Rule duplicated successfully');
      onRuleUpdated();
    } catch (error) {
      console.error('Error duplicating rule:', error);
      toast.error('Failed to duplicate rule');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePause = async () => {
    const newState = !isActive;
    setIsActive(newState);
    
    try {
      const { error } = await supabase
        .from('filtering_rules')
        .update({ is_active: newState })
        .eq('id', rule?.id);

      if (error) throw error;
      
      toast.success(newState ? 'Rule resumed' : 'Rule paused');
      onRuleUpdated();
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update rule status');
      setIsActive(!newState); // Revert on error
    }
  };

  if (!rule) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings2 className="h-5 w-5" />
            <span>Rule Settings: {rule.name}</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="general" className="flex items-center space-x-2">
              <Edit3 className="h-4 w-4" />
              <span>General</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Schedule</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center space-x-2">
              <Bell className="h-4 w-4" />
              <span>Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </TabsTrigger>
          </TabsList>

          {/* General Settings Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="ruleName">Rule Name</Label>
                    <Input
                      id="ruleName"
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                      placeholder="Enter rule name"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label>Rule Status</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={isActive}
                        onCheckedChange={setIsActive}
                      />
                      <span className="text-sm text-muted-foreground">
                        {isActive ? 'Active' : 'Paused'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <Label>Priority Level</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low Priority</SelectItem>
                        <SelectItem value="medium">Medium Priority</SelectItem>
                        <SelectItem value="high">High Priority</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Rule Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Type</Label>
                    <Badge
                      variant={
                        rule.rule_type === 'listening' || rule.rule_type === 'monitor'
                          ? 'default'
                          : 'destructive'
                      }
                    >
                      {rule.rule_type === 'listening' || rule.rule_type === 'monitor'
                        ? 'Listening'
                        : rule.rule_type === 'blocking'
                          ? 'Blocking'
                          : 'Filter'}
                    </Badge>
                  </div>
                  
                  <div>
                    <Label>Platforms</Label>
                    <div className="flex flex-wrap gap-1">
                      {rule.platforms?.map((platform) => (
                        <Badge key={platform} variant="secondary" className="text-xs">
                          {platform}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {rule.keywords && rule.keywords.length > 0 && (
                    <div>
                      <Label>Keywords</Label>
                      <div className="flex flex-wrap gap-1">
                        {rule.keywords.map((keyword, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedule Settings Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Time Restrictions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Enable Time Restrictions</Label>
                  <Switch
                    checked={hasTimeRestrictions}
                    onCheckedChange={setHasTimeRestrictions}
                  />
                </div>

                {hasTimeRestrictions && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={activeHours.start}
                          onChange={(e) => setActiveHours(prev => ({ ...prev, start: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={activeHours.end}
                          onChange={(e) => setActiveHours(prev => ({ ...prev, end: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Active Days</Label>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div key={day} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={day}
                              checked={activeDays.includes(day)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setActiveDays([...activeDays, day]);
                                } else {
                                  setActiveDays(activeDays.filter(d => d !== day));
                                }
                              }}
                            />
                            <Label htmlFor={day} className="text-sm capitalize">
                              {day.slice(0, 3)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rule Notifications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when this rule is triggered
                    </p>
                  </div>
                  <Switch
                    checked={enableNotifications}
                    onCheckedChange={setEnableNotifications}
                  />
                </div>

                {enableNotifications && (
                  <div>
                    <Label>Notification Threshold</Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Send notification after this many triggers per hour
                    </p>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={notificationThreshold}
                      onChange={(e) => setNotificationThreshold(parseInt(e.target.value) || 1)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Items Filtered</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.itemsFiltered}</div>
                  <p className="text-xs text-muted-foreground">Total processed</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Last Triggered</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    {stats.lastTriggered 
                      ? new Date(stats.lastTriggered).toLocaleDateString()
                      : 'Never'
                    }
                  </div>
                  <p className="text-xs text-muted-foreground">Most recent match</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Rule Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant={isActive ? 'default' : 'secondary'}>
                    {isActive ? 'Active' : 'Paused'}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">Current state</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <Separator />
        <div className="flex flex-wrap gap-3 justify-between">
          <div className="flex gap-2">
            <Button onClick={handleUpdateRule} disabled={loading}>
              <Edit3 className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleTogglePause}>
              {isActive ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause Rule
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Resume Rule
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleDuplicateRule}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
          </div>
          
          <Button variant="destructive" onClick={handleDeleteRule} disabled={loading}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Rule
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RuleSettingsDialog;